import { useSwivuKanban, createSyntheticGroupRecord } from "@/react/hooks/useSwivuKanban";
import { Kanban, KanbanBoard, KanbanColumn, KanbanItem, KanbanItemHandle, KanbanOverlay, useKanbanColumnContext, type KanbanColumnType } from "@/modern/components/ui/kanban";
import { Badge } from "@/modern/components/ui/badge";
import { Button } from "@/modern/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/modern/components/ui/collapsible";
import { ChevronRight, ChevronLeft, GripVertical, LayoutDashboard, X, MoreVertical } from "lucide-react";
import { cn } from "../utils";
import { useVirtualizer } from "@tanstack/react-virtual";
import React, { useRef, useCallback, useEffect, useState } from "react";
import { getFontSize } from "@/react/context/bryntumContext";
import { Hitbox } from "./ui/hitbox";
import { getOriginalRecord } from "@/other/utils/utils";
import { Menu } from "@bryntum/schedulerpro";
import { KanbanCardContent } from "@/modern/components/kanban/KanbanCardContent";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";

const COLUMN_GAP_PX = 16;
const COLLAPSED_COLUMN_WIDTH_PX = 64;

function KanbanEmptyState({ showIcon = true, message, description, variant = "board" }: { showIcon?: boolean; message: string; description?: string; variant?: "board" | "column" }) {
  const isBoard = variant === "board";
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center text-center",
        isBoard ? "h-full m-4" : "py-12 px-4"
      )}
    >
      <div
        className={cn(
          "flex flex-col items-center gap-6",
          isBoard
            ? "rounded-2xl border border-dashed border-muted-foreground/30 bg-muted/40 px-8 py-8 shadow-sm w-full max-w-sm"
            : "gap-3"
        )}
      >
        {showIcon && <div
          className={cn(
            "flex items-center justify-center rounded-full",
            isBoard ? "mx-auto h-12 w-12 bg-primary/10 text-primary" : "h-12 w-12 bg-muted/50 text-muted-foreground"
          )}
        >
          <LayoutDashboard className="size-6" strokeWidth={1.5} />
        </div>}
        <div className="space-y-2">
          <h3
            className={cn(
              "text-base font-semibold text-foreground",
              !isBoard && "text-sm font-medium text-muted-foreground"
            )}
          >
            {message}
          </h3>
          {description && <p className="text-sm text-muted-foreground">{description}</p>}
        </div>
      </div>
    </div>
  );
}

interface KanbanWrapperProps extends ReturnType<typeof useSwivuKanban> {
  itemRenderer?: (data: any) => React.ReactNode;
  emptyText?: string;
  onItemDblClick?: (item: any) => void;
  grid?: any;
  processCellMenuItems?: (e: any) => void;
}


const getItemValueStable = (item: any) => item.id;

export function KanbanWrapper(props: KanbanWrapperProps) {
  const { columns, handleChange, handlePersist, toggleColumnCollapse, collapsedColumns, isKanbanMode, itemRenderer: itemRendererProp, emptyText = "No items", onItemDblClick, removeFromCurrentColumn, getFilteredHiddenCountByColumnId, grid, processCellMenuItems, activeConfig, entityType, visibleFieldIds } = props;

  const { project, userPreferences } = useSwivuBryntumContext();

  const defaultItemRenderer = useCallback(
    (data: any) => (
      <KanbanCardContent
        entityType={entityType}
        record={getOriginalRecord(data)}
        visibleFieldIds={visibleFieldIds ?? []}
        grouperField={activeConfig?.grouperField}
        project={project}
        userPreferences={userPreferences}
      />
    ),
    [entityType, visibleFieldIds, activeConfig?.grouperField, project, userPreferences]
  );

  const itemRenderer = itemRendererProp ?? (entityType ? defaultItemRenderer : undefined);

  // Debounce hidden count to avoid a brief "1 hidden" flash when moving array-value items:
  // the store updates immediately on drop but columns state updates async, so for one frame
  // totalInColumn (from store) > visibleInColumn (from stale columns) and the badge appears then disappears.
  const [filteredHiddenByColumn, setFilteredHiddenByColumn] = useState<Record<string, number>>({});
  useEffect(() => {
    if (!columns?.length) {
      setFilteredHiddenByColumn({});
      return;
    }
    const t = window.setTimeout(() => {
      setFilteredHiddenByColumn(getFilteredHiddenCountByColumnId(columns));
    }, 350);
    return () => window.clearTimeout(t);
  }, [columns, getFilteredHiddenCountByColumnId]);

  const parentRef = useRef<HTMLDivElement>(null);
  const getItemValue = useCallback(getItemValueStable, []);

  const expandedWidth = getFontSize() * 20;
  const virtualizer = useVirtualizer({
    count: columns?.length ?? 0,
    getScrollElement: () => parentRef.current,
    estimateSize: () => expandedWidth + COLUMN_GAP_PX,
    overscan: 4,
    horizontal: true
  });

  // Update virtualizer sizes when collapse state changes (including when synced from grid)
  useEffect(() => {
    if (!columns || columns.length === 0) return;

    columns.forEach((column, index) => {
      const isCollapsed = collapsedColumns[column.id];
      const width = (isCollapsed ? COLLAPSED_COLUMN_WIDTH_PX : expandedWidth) + COLUMN_GAP_PX;
      virtualizer.resizeItem(index, width);
    });
  }, [collapsedColumns, columns, expandedWidth, virtualizer]);

  const handleColumnHeaderMenuClick = useCallback((event: React.MouseEvent, column: KanbanColumnType<any>) => {
    // Guard: Ensure grid and menu processor are available
    if (!grid || !processCellMenuItems || !activeConfig) return;

    const groupField = activeConfig.grouperField;
    const isCollapsed = collapsedColumns[column.id] || false;

    // Create synthetic group record - ensure it mimics grid group header exactly
    const syntheticRecord = createSyntheticGroupRecord(column, grid, groupField, isCollapsed);

    // Get the cellMenu feature to access its items
    const cellMenuFeature = grid.features.cellMenu;
    if (!cellMenuFeature || !cellMenuFeature.items) return;

    // Clone the menu items to avoid modifying the original config
    // We need a deep-ish clone to ensure menu item properties are independent
    const menuItemsClone: any = {};
    Object.keys(cellMenuFeature.items).forEach(key => {
      const item = cellMenuFeature.items[key];
      if (item && typeof item === 'object') {
        menuItemsClone[key] = { ...item };
      } else {
        menuItemsClone[key] = item;
      }
    });

    // Process items using existing grid logic BEFORE creating the menu
    // This allows us to modify the items config before Menu widget processes them
    try {
      processCellMenuItems({
        items: menuItemsClone,
        record: syntheticRecord,
        selection: [],
        source: cellMenuFeature
      });
    } catch (error) {
      console.error('Error processing menu items:', error);
      return;
    }

    // Override collapse/expand handlers for kanban mode
    // These need to use kanban's toggle methods, not grid methods
    if (menuItemsClone.collapseRow && !menuItemsClone.collapseRow.hidden) {
      // Log the current state and text
      menuItemsClone.collapseRow.onItem = () => {
        // Find the column index in the columns array
        const columnIndex = columns?.findIndex(col => col.id === column.id) ?? -1;

        // Toggle the collapse state in kanban
        toggleColumnCollapse(String(column.id));
        const willBeCollapsed = !isCollapsed; // Will be collapsed after toggle

        // Update virtualizer size to match the new state
        if (columnIndex >= 0) {
          const width = (willBeCollapsed ? COLLAPSED_COLUMN_WIDTH_PX : expandedWidth) + COLUMN_GAP_PX;
          virtualizer.resizeItem(columnIndex, width);
        }

        // Sync collapse state to grid using the same logic as the collapse button
        if (grid?.store?.groupHeaderRecords && activeConfig) {
          const groupHeader = grid.store.groupHeaderRecords.find((gh: any) => {
            const firstChild = gh.groupChildren?.[0];
            if (!firstChild) {
              return false;
            }
            const numericValue = firstChild[activeConfig.setterField];

            // For N/A column, match against both null and empty array
            if (column.value === null) {
              return numericValue === null || (Array.isArray(numericValue) && numericValue.length === 0);
            }
            // For array fields (like tags), check if column value is in the array
            // For single value fields, use direct comparison
            return Array.isArray(numericValue) ? numericValue.includes(column.value) : numericValue === column.value;
          });

          if (groupHeader && grid.features.group) {
            // Check if states are mismatched - only toggle if needed
            const shouldToggle = (willBeCollapsed && !groupHeader.meta.collapsed) || (!willBeCollapsed && groupHeader.meta.collapsed);

            if (shouldToggle) {
              grid.features.group.toggleCollapse(groupHeader);
            }
          }
        }
      };
    }

    if (menuItemsClone.collapseAllRows && !menuItemsClone.collapseAllRows.hidden) {
      // Check if all columns are collapsed
      const allColumnIds = columns?.map(col => col.id) || [];
      const isAllCollapsed = allColumnIds.every(id => collapsedColumns[id]);

      // Update text and icon based on state
      if (isAllCollapsed) {
        menuItemsClone.collapseAllRows.text = "Expand All";
        menuItemsClone.collapseAllRows.icon = "fa-solid fa-chevrons-down";
      } else {
        menuItemsClone.collapseAllRows.text = "Collapse All";
        menuItemsClone.collapseAllRows.icon = "fa-solid fa-chevrons-up";
      }

      menuItemsClone.collapseAllRows.onItem = () => {
        // Toggle all columns
        allColumnIds.forEach((id, index) => {
          const currentlyCollapsed = collapsedColumns[id];

          // If expanding all, only expand collapsed ones
          // If collapsing all, only collapse expanded ones
          if (isAllCollapsed && currentlyCollapsed) {
            toggleColumnCollapse(String(id));
            // Update virtualizer size to expanded
            const width = expandedWidth + COLUMN_GAP_PX;
            virtualizer.resizeItem(index, width);
          } else if (!isAllCollapsed && !currentlyCollapsed) {
            toggleColumnCollapse(String(id));
            // Update virtualizer size to collapsed
            const width = COLLAPSED_COLUMN_WIDTH_PX + COLUMN_GAP_PX;
            virtualizer.resizeItem(index, width);
          }
        });

        // Also apply collapse/expand all to the grid
        if (grid?.features?.group) {
          if (isAllCollapsed) {
            // Expand all groups in grid
            grid.expandAll();
          } else {
            // Collapse all groups in grid
            grid.collapseAll();
          }
        }
      };
    }

    // Fix for empty columns: Hide archive/remove items if column has no records
    const hasRecords = column.data && column.data.length > 0;
    if (!hasRecords) {
      if (menuItemsClone.archive) {
        menuItemsClone.archive.hidden = true;
      }
      if (menuItemsClone.removeRow) {
        menuItemsClone.removeRow.hidden = true;
      }
      if (menuItemsClone.copyTo) {
        menuItemsClone.copyTo.hidden = true;
      }
    }

    // NOW create the menu with the processed items
    // The Menu widget should respect the hidden property
    const menu = new Menu({
      items: menuItemsClone,
      forElement: event.currentTarget as HTMLElement,
      listeners: {
        hide: () => {
          // Clean up menu after it's hidden
          setTimeout(() => menu.destroy(), 100);
        }
      }
    });

    // Show menu positioned relative to the button
    menu.showBy(event.currentTarget as HTMLElement);
  }, [grid, processCellMenuItems, activeConfig, collapsedColumns, toggleColumnCollapse, columns, expandedWidth, virtualizer]);

  const virtualColumns = virtualizer.getVirtualItems();


  if (!isKanbanMode) {
    return null;
  }

  if (!columns || columns.length === 0) {
    return <KanbanEmptyState message="No columns found" description="Use settings to manage columns for this grouper." variant="board" />;
  }

  return (
    <Kanban
      columns={columns}
      onColumnsChange={handleChange}
      onColumnsPersist={handlePersist}
      getItemValue={getItemValue}
      disableItemSorting={true}
      flatCursor={true}
    >
      <KanbanBoard className="flex flex-col px-4 py-4">
        <div
          ref={parentRef}
          className="flex-1 min-h-0 overflow-x-auto overflow-y-hidden"
          style={{ width: "100%", height: "100%" }}
        >
          <div
            style={{
              width: `${virtualizer.getTotalSize()}px`,
              height: "100%",
              position: "relative",
            }}
          >
            {virtualColumns.map((virtualColumn) => {
              const column = columns[virtualColumn.index];
              const isCollapsed = collapsedColumns[column.id];
              return (
                <Collapsible
                  key={column.id}
                  open={!isCollapsed}
                  onOpenChange={(open) => {
                    toggleColumnCollapse(String(column.id));
                    const willBeCollapsed = !open;
                    const width = (willBeCollapsed ? COLLAPSED_COLUMN_WIDTH_PX : expandedWidth) + COLUMN_GAP_PX;
                    virtualizer.resizeItem(virtualColumn.index, width);

                    // Sync collapse state to grid
                    if (grid?.store?.groupHeaderRecords && activeConfig) {
                      // Find the matching group header by extracting numeric ID from first child
                      // Grid group headers store the display title, not numeric ID
                      const groupHeader = grid.store.groupHeaderRecords.find((gh: any) => {
                        const firstChild = gh.groupChildren?.[0];
                        if (!firstChild) {
                          return false;
                        }
                        const numericValue = firstChild[activeConfig.setterField];

                        // For N/A column, match against both null and empty array
                        if (column.value === null) {
                          return numericValue === null || (Array.isArray(numericValue) && numericValue.length === 0);
                        }
                        // For array fields (like tags), check if column value is in the array
                        // For single value fields, use direct comparison
                        return Array.isArray(numericValue) ? numericValue.includes(column.value) : numericValue === column.value;
                      });

                      if (groupHeader && grid.features.group) {
                        // Check if states are mismatched - only toggle if needed
                        const shouldToggle = (willBeCollapsed && !groupHeader.meta.collapsed) || (!willBeCollapsed && groupHeader.meta.collapsed);

                        if (shouldToggle) {
                          grid.features.group.toggleCollapse(groupHeader);
                        }
                      }
                    }
                  }}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    height: "100%",
                    width: `${virtualColumn.size}px`,
                    transform: `translateX(${virtualColumn.start}px)`
                  }}
                >
                  <KanbanColumn value={column.id} className={cn(isCollapsed ? "w-16" : "w-[20rem]", "transition-all duration-300 bg-muted border rounded-sm p-0 h-full")} style={{ marginRight: COLUMN_GAP_PX }}>
                    <div className={cn("flex items-center justify-between border-b py-3 px-4", isCollapsed ? "flex-col-reverse py-4 gap-2 border-b-0" : "")}>
                      <div className={cn("flex items-center gap-3 min-w-0 flex-1", isCollapsed ? "flex-col" : "")}>
                        <span title={column.title} className={cn("font-semibold text-sm", isCollapsed ? "[writing-mode:vertical-lr]" : "truncate min-w-0")}>{column.title}</span>
                        <div className={cn("flex items-center gap-1.5", isCollapsed && "flex-col justify-center")}>
                          <Badge variant="muted" size="sm" className={cn("leading-none border-none", isCollapsed && "min-w-0 px-1.5")}>
                            {column.data.length}
                          </Badge>
                          {filteredHiddenByColumn[column.id] != null && filteredHiddenByColumn[column.id] > 0 && (
                            <Badge variant="secondary" size="sm" className={cn("leading-none border-none px-1.5 py-1 text-xs bg-amber-500/20 hover:bg-amber-500/20 text-amber-600 dark:text-amber-400 truncate", isCollapsed && "px-1 min-w-0")} title={`${filteredHiddenByColumn[column.id]} hidden`}>
                              {isCollapsed ? `${filteredHiddenByColumn[column.id]}h` : `${filteredHiddenByColumn[column.id]} hidden`}
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className={cn("flex items-center", isCollapsed ? "flex-col gap-1" : "gap-1")}>
                        {grid && processCellMenuItems && (
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            onClick={(e) => handleColumnHeaderMenuClick(e, column)}
                            title="Column menu"
                          >
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        )}
                        <CollapsibleTrigger asChild>
                          <Button variant="ghost" size="icon-sm">
                            {isCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
                          </Button>
                        </CollapsibleTrigger>
                      </div>
                    </div>
                    <CollapsibleContent className="flex flex-col gap-3 h-full overflow-hidden">
                      {column.data && column.data.length > 0 ? (
                        <VirtualizedColumnContent
                          data={column.data}
                          columnValue={column.value}
                          itemRenderer={itemRenderer}
                          onItemDblClick={onItemDblClick}
                          onRemoveFromColumn={removeFromCurrentColumn}
                        />
                      ) : (
                        <KanbanEmptyState showIcon={false} message={emptyText} variant="column" />
                      )}
                    </CollapsibleContent>
                  </KanbanColumn>
                </Collapsible>
              );
            })}
          </div>
        </div>
      </KanbanBoard>
      <KanbanOverlay>
        {({ value, variant, getItemBaseId, isOverInvalidDropTarget }) => {
          if (variant === "column") {
            const column = columns.find(col => col.id === value);
            if (!column) return null;
            const isCollapsed = collapsedColumns[column.id];

            return (
              <KanbanColumn value={column.id} className={isCollapsed ? "w-16" : ""}>
                <div className={`flex items-center justify-between border-b px-3 pb-3 mb-3 ${isCollapsed ? "flex-col py-4 gap-2 border-b-0" : ""}`}>
                  <div className={`flex items-center gap-2 ${isCollapsed ? "flex-col" : ""}`}>
                    <span className={`font-semibold text-sm ${isCollapsed ? "[writing-mode:vertical-lr] rotate-180" : ""}`}>{column.title}</span>
                    <div className="flex items-center gap-1.5">
                      <Badge variant="primary-outline" size="sm">
                        {column.data.length}
                      </Badge>
                      {filteredHiddenByColumn[column.id] != null && filteredHiddenByColumn[column.id] > 0 && (
                        <Badge variant="secondary" size="sm" className="leading-none border-none px-1.5 py-1 text-xs bg-amber-500/20 text-amber-600 dark:text-amber-400">
                          {filteredHiddenByColumn[column.id]} hidden
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              </KanbanColumn>
            );
          }

          // variant === "item" — value may be composite (columnId__itemId) when same item is in multiple columns
          const itemId = getItemBaseId ? getItemBaseId(value) : value;
          const allItems = columns.flatMap((col) => col.data);
          const idMatch = (d: any, id: unknown) => {
            const rid = d?.id ?? d?.get?.("id");
            return rid == id || String(rid) === String(id);
          };
          let data = allItems.find((d) => idMatch(d, itemId));
          if (!data && value !== itemId) {
            data = allItems.find((d) => idMatch(d, value));
          }
          return (
            <div
              className={cn(
                "min-w-[200px] min-h-[60px] overflow-hidden rounded-sm",
                isOverInvalidDropTarget && "ring-2 ring-destructive"
              )}
              style={{ opacity: 1 }}
            >
              {data ? (
                <>
                  <KanbanItemContent data={data} itemRenderer={itemRenderer} isOverlay />
                  {isOverInvalidDropTarget && <div className="absolute -top-7 left-1 rounded-md bg-destructive text-destructive-foreground text-xs px-2 py-1 rounded-xs" >Entity already exists in this column</div>}
                </>
              ) : (
                <div className="p-3 text-sm text-muted-foreground border border-border rounded-sm bg-background dark:bg-background/80">Dragging…</div>
              )}
            </div>
          );
        }}
      </KanbanOverlay>
    </Kanban>
  );
}

function getItemDataId(data: any): string | number | null {
  if (data == null) return null;
  const id = data?.id ?? data?.get?.("id");
  return id != null ? String(id) : null;
}

const VirtualizedColumnContent = React.memo(function VirtualizedColumnContent({
  data,
  columnValue,
  itemRenderer,
  onItemDblClick,
  onRemoveFromColumn,
}: {
  data: any[];
  columnValue: any;
  itemRenderer?: (data: any) => React.ReactNode;
  onItemDblClick?: (item: any) => void;
  onRemoveFromColumn?: (itemRecord: any, columnValue: any) => void;
}) {
  const parentRef = useRef<HTMLDivElement>(null);
  const { getItemIdForItem } = useKanbanColumnContext("VirtualizedColumnContent");

  const virtualizer = useVirtualizer({
    count: data.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 120,
    overscan: 5,
  });

  const items = virtualizer.getVirtualItems();

  return (
    <div
      ref={parentRef}
      className="flex-1 overflow-y-auto"
      style={{ height: '100%' }}
    >
      <div
        style={{
          height: `${virtualizer.getTotalSize()}px`,
          width: '100%',
          position: 'relative',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            transform: `translateY(${items[0]?.start ?? 0}px)`,
          }}
        >
          {items.map((virtualItem) => {
            const itemData = data[virtualItem.index];
            const itemId = getItemIdForItem(itemData);
            return (
              <div
                key={itemId}
                ref={virtualizer.measureElement}
                data-index={virtualItem.index}
                className="px-3 pb-3"
              >
                <KanbanItem value={itemId}>
                  <KanbanItemContent
                    data={itemData}
                    columnValue={columnValue}
                    itemRenderer={itemRenderer}
                    onItemDblClick={onItemDblClick}
                    onRemoveFromColumn={onRemoveFromColumn}
                  />
                </KanbanItem>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
});

function KanbanItemContentInner({
  data,
  columnValue,
  itemRenderer,
  onItemDblClick,
  onRemoveFromColumn,
  isOverlay = false,
}: {
  data: any;
  columnValue?: any;
  itemRenderer?: (data: any) => React.ReactNode;
  onItemDblClick?: (item: any) => void;
  onRemoveFromColumn?: (itemRecord: any, columnValue: any) => void;
  isOverlay?: boolean;
}) {
  const grip = (
    <span className="flex items-center cursor-grab active:cursor-grabbing touch-none text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity [&:focus]:opacity-100">
      <GripVertical className="h-4 w-4" aria-hidden />
    </span>
  );

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    const record = getOriginalRecord(data);
    onRemoveFromColumn?.(record, columnValue);
  };

  const showChromeActions =
    !isOverlay && columnValue != null && onRemoveFromColumn != null;

  return (
    <div className="bg-background dark:bg-background/80 rounded-sm p-4 border border-border shadow-xs flex items-start gap-2 group relative overflow-hidden cursor-pointer" onDoubleClick={isOverlay ? undefined : (onItemDblClick ? () => onItemDblClick(getOriginalRecord(data)) : undefined)}>
      {isOverlay ? grip : (
        <Hitbox size="lg">
          <KanbanItemHandle className="cursor-grab" asChild>
            {grip}
          </KanbanItemHandle>
        </Hitbox>
      )}
      <div className="flex-1 min-w-0">
        {itemRenderer ? itemRenderer(data) : (
          <h4 className={cn("text-sm font-medium leading-snug text-foreground line-clamp-2", data?.archived && "line-through")}>
            {data.name || "N/A"}
          </h4>
        )}
      </div>
      {showChromeActions && (
        <div className="absolute top-0 right-0 flex opacity-0 group-hover:opacity-100 transition-opacity">
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            className="rounded-bl-md rounded-tr-none rounded-tl-none rounded-br-none shrink-0 size-6"
            onClick={handleRemove}
            aria-label="Remove from column"
          >
            <X className="h-3 w-3" />
          </Button>
        </div>
      )}
    </div>
  );
}

const KanbanItemContent = React.memo(KanbanItemContentInner, (prev, next) => {
  if (prev.isOverlay !== next.isOverlay) return false;
  if (prev.columnValue !== next.columnValue) return false;
  if (getItemDataId(prev.data) !== getItemDataId(next.data)) return false;
  if (prev.data !== next.data) return false;
  return (
    prev.itemRenderer === next.itemRenderer &&
    prev.onItemDblClick === next.onItemDblClick &&
    prev.onRemoveFromColumn === next.onRemoveFromColumn
  );
});